/**
 * Encrypted localStorage utility using AES-GCM (Web Crypto API).
 * All values are JSON-serialized before encryption so any type is supported.
 *
 * Usage:
 *   import { setItem, getItem, removeItem } from '../utils/secureStorage';
 *
 *   await setItem('user', { id: 1, name: 'Anas' });
 *   const user = await getItem('user');   // { id: 1, name: 'Anas' }
 *   removeItem('user');
 */

// Change this secret in .env as REACT_APP_STORAGE_SECRET
const SECRET = process.env.REACT_APP_STORAGE_SECRET || 'erc-secure-default-key-2024';
const SALT   = 'erc-storage-salt-v1';

// Cache the derived key so PBKDF2 only runs once per session
let _keyCache = null;

async function getDerivedKey() {
  if (_keyCache) return _keyCache;

  const encoder = new TextEncoder();

  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(SECRET),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  _keyCache = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(SALT),
      iterations: 100_000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return _keyCache;
}

/**
 * Encrypts and stores a value in localStorage.
 * @param {string} key
 * @param {*} value  - Any JSON-serializable value
 */
export async function setItem(key, value) {
  try {
    const cryptoKey = await getDerivedKey();
    const iv        = window.crypto.getRandomValues(new Uint8Array(12));
    const encoded   = new TextEncoder().encode(JSON.stringify(value));

    const cipherBuf = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encoded
    );

    // Pack: [12-byte IV] + [ciphertext]
    const packed = new Uint8Array(12 + cipherBuf.byteLength);
    packed.set(iv, 0);
    packed.set(new Uint8Array(cipherBuf), 12);

    localStorage.setItem(key, btoa(String.fromCharCode(...packed)));
  } catch (err) {
    console.error('[secureStorage] setItem failed:', err);
  }
}

/**
 * Reads and decrypts a value from localStorage.
 * Returns null if the key is missing or decryption fails.
 * @param {string} key
 * @returns {Promise<*>}
 */
export async function getItem(key) {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const packed    = Uint8Array.from(atob(stored), c => c.charCodeAt(0));
    const iv        = packed.slice(0, 12);
    const cipher    = packed.slice(12);
    const cryptoKey = await getDerivedKey();

    const plainBuf = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      cipher
    );

    return JSON.parse(new TextDecoder().decode(plainBuf));
  } catch (_decryptErr) {
    // Decryption failed — value was likely stored unencrypted by the previous deployment.
    // Attempt a plain JSON parse as a one-time migration fallback.
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (parseErr) {
      console.warn('[secureStorage] fallback parse failed:', parseErr);
      return null;
    }
  }
}

/**
 * Removes a single key from localStorage.
 * @param {string} key
 */
export function removeItem(key) {
  localStorage.removeItem(key);
}

/**
 * Clears ALL keys from localStorage.
 */
export function clearAll() {
  localStorage.clear();
}
